/**
 * CP-8.3 — Admin Notification Center + Email Template Gallery.
 *
 * Three rolling boards driven by /admin/notifications/timeline:
 *  - Dispatched  (notification.dispatched + notification.delivered)
 *  - Suppressed  (notification.suppressed)
 *  - Failed      (notification.delivery_failed)
 *
 * Each card opens a slide-out drilldown panel showing:
 *  - source audit event id + event_type
 *  - original notification row (recipient, channel, category, state)
 *  - suppression reason or delivery error
 *  - provider_id + provider_msg_id when delivered
 *
 * Email template gallery (separate tab):
 *  - Lists templates from GET /admin/notifications/templates
 *  - Renders preview via POST /admin/notifications/templates/{et}/preview
 *  - Shows subject + HTML iframe + text body
 *
 * Workforce Partners brand-consistent throughout.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, AlertTriangle, ShieldOff, ExternalLink, X,
         RefreshCw, FileText, Play, Send, Inbox, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";


function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  try { return d.toLocaleDateString(); } catch { return iso; }
}


// ===========================================================================
// Main tab — Notification Observability
// ===========================================================================

export function AdminNotificationsTab() {
  const [tab, setTab] = useState("observability");
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 w-fit">
        <button
          type="button"
          onClick={() => setTab("observability")}
          data-testid={TID.adminNotifTab}
          className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition ${
            tab === "observability" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
          }`}
        >
          <Inbox className="w-4 h-4" /> Observability
        </button>
        <button
          type="button"
          onClick={() => setTab("templates")}
          data-testid={TID.adminTemplatesTab}
          className={`flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-md transition ${
            tab === "templates" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
          }`}
        >
          <FileText className="w-4 h-4" /> Email templates
        </button>
      </div>
      {tab === "observability" ? <ObservabilityBoards /> : <TemplateGallery />}
    </div>
  );
}


// ===========================================================================
// Observability boards
// ===========================================================================

function ObservabilityBoards() {
  const [dispatched, setDispatched] = useState([]);
  const [suppressed, setSuppressed] = useState([]);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const [d, s, f] = await Promise.all([
        api("/admin/notifications/timeline?event_type=notification.delivered&limit=100"),
        api("/admin/notifications/timeline?event_type=notification.suppressed&limit=100"),
        api("/admin/notifications/timeline?event_type=notification.delivery_failed&limit=100"),
      ]);
      setDispatched(d.data || []);
      setSuppressed(s.data || []);
      setFailed(f.data || []);
    } catch (e) {
      toast.error(e.message || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function runWorker(label, path) {
    try {
      const r = await api(path, { method: "POST" });
      toast.success(`${label}: ${JSON.stringify(r)}`);
      refresh();
    } catch (e) {
      toast.error(e.message || `Failed to run ${label}`);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">
            Notification observability
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Read-only audit-timeline view of every notification dispatched,
            suppressed, or failed across the platform.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            data-testid={TID.adminNotifRefresh}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            type="button"
            onClick={() => runWorker("Dispatcher", "/admin/notifications/run-dispatcher")}
            data-testid={TID.adminNotifRunDispatcher}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition"
          >
            <Play className="w-3.5 h-3.5" /> Dispatcher
          </button>
          <button
            type="button"
            onClick={() => runWorker("Email worker", "/admin/notifications/run-email-worker")}
            data-testid={TID.adminNotifRunEmailWorker}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition"
          >
            <Send className="w-3.5 h-3.5" /> Email worker
          </button>
          <button
            type="button"
            onClick={() => runWorker("Digester", "/admin/notifications/run-digester")}
            data-testid={TID.adminNotifRunDigester}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition"
          >
            <Mail className="w-3.5 h-3.5" /> Digester
          </button>
          <button
            type="button"
            onClick={() => runWorker("Retry failed", "/admin/notifications/retry-failed")}
            data-testid={TID.adminNotifRetryFailed}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-700 transition"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Retry failed
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Board
          testid={TID.adminNotifBoardDispatched}
          tone="primary"
          title="Dispatched"
          count={dispatched.length}
          icon={<Send className="w-4 h-4" />}
          items={dispatched}
          onSelect={setSelected}
          loading={loading}
          pillCls="bg-blue-100 text-blue-800"
          labelFor={(row) => `${row.data?.event_type || row.event_type}`}
        />
        <Board
          testid={TID.adminNotifBoardSuppressed}
          tone="muted"
          title="Suppressed"
          count={suppressed.length}
          icon={<ShieldOff className="w-4 h-4" />}
          items={suppressed}
          onSelect={setSelected}
          loading={loading}
          pillCls="bg-slate-200 text-slate-700"
          labelFor={(row) => row.data?.reason || "suppressed"}
        />
        <Board
          testid={TID.adminNotifBoardFailed}
          tone="danger"
          title="Failed"
          count={failed.length}
          icon={<AlertTriangle className="w-4 h-4" />}
          items={failed}
          onSelect={setSelected}
          loading={loading}
          pillCls="bg-red-100 text-red-700"
          labelFor={(row) => row.data?.error_kind || "delivery_failed"}
        />
      </div>

      {selected && <DrilldownPanel row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}


function Board({ testid, title, count, icon, items, onSelect, loading, pillCls, labelFor }) {
  return (
    <section
      data-testid={testid}
      className="flex flex-col gap-3 rounded-lg bg-slate-50/50 border border-slate-200 p-4 max-h-[70vh] overflow-y-auto"
    >
      <header className="flex items-center justify-between sticky top-0 z-10 bg-slate-50/95 backdrop-blur pb-2 -mt-2 pt-2 border-b border-slate-200">
        <div className="flex items-center gap-2 font-semibold text-slate-800 text-sm">
          {icon} {title}
        </div>
        <span className="text-xs text-slate-500">{count}</span>
      </header>
      {loading && (
        <div className="text-center text-xs text-slate-400 py-6">Loading…</div>
      )}
      {!loading && items.length === 0 && (
        <div className="text-center text-xs text-slate-400 py-6">Nothing here.</div>
      )}
      {items.map((row) => (
        <button
          key={row.id}
          type="button"
          onClick={() => onSelect(row)}
          data-testid={TID.adminNotifCard}
          className="text-left flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-[var(--avcp-primary)] hover:shadow-md transition"
        >
          <div className="flex items-center justify-between gap-2">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${pillCls}`}>
              {labelFor(row)}
            </span>
            <span className="text-[10px] text-slate-400">{fmtRel(row.occurred_at)}</span>
          </div>
          <div className="text-sm font-medium text-slate-900 truncate">
            {row.data?.source_event_type || row.data?.event_type || "—"}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <span>{row.data?.category || "—"}</span>
            <ArrowRight className="w-3 h-3" />
            <span>user #{row.data?.recipient_user_id || "—"}</span>
            <span className="text-slate-400">· {row.data?.channel || "—"}</span>
          </div>
        </button>
      ))}
    </section>
  );
}


function DrilldownPanel({ row, onClose }) {
  return (
    <div
      data-testid={TID.adminNotifDrilldown}
      className="fixed right-0 top-0 h-full w-full sm:w-[450px] border-l border-slate-200 bg-white shadow-2xl flex flex-col z-50"
    >
      <header className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            {row.event_type}
          </div>
          <div className="font-semibold text-slate-900 mt-1">
            audit event #{row.id}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid={TID.adminNotifDrilldownClose}
          className="p-1.5 rounded-full hover:bg-slate-200 transition"
          aria-label="Close"
        >
          <X className="w-4 h-4 text-slate-600" />
        </button>
      </header>
      <div className="p-5 overflow-y-auto flex-1 space-y-5">
        <Section title="Source event">
          <KV k="audit event id" v={`#${row.id}`} />
          <KV k="event type" v={row.event_type} />
          <KV k="occurred at" v={row.occurred_at} />
          <KV k="resource" v={`${row.resource_kind} #${row.resource_id || "—"}`} />
        </Section>
        <Section title="Notification">
          <KV k="source event type" v={row.data?.source_event_type || row.data?.event_type} />
          <KV k="category" v={row.data?.category} />
          <KV k="recipient user id" v={row.data?.recipient_user_id} />
          <KV k="recipient kind" v={row.data?.recipient_kind || "user"} />
          <KV k="channel(s)" v={(row.data?.channels || [row.data?.channel]).filter(Boolean).join(", ")} />
          {row.data?.rate_limited && <KV k="rate-limited" v="yes (demoted to digest)" />}
          {row.data?.unread_lock && <KV k="sticky lock" v="yes (review required)" />}
        </Section>
        {row.event_type === "notification.suppressed" && (
          <Section title="Suppression">
            <KV k="reason" v={row.data?.reason} highlight />
          </Section>
        )}
        {row.event_type === "notification.delivery_failed" && (
          <Section title="Failure">
            <KV k="error kind" v={row.data?.error_kind} highlight />
            <KV k="error message" v={row.data?.error_message} />
            <KV k="provider" v={row.data?.provider_id} />
            <KV k="retryable" v={String(row.data?.retryable)} />
          </Section>
        )}
        {row.event_type === "notification.delivered" && (
          <Section title="Delivery">
            <KV k="provider" v={row.data?.provider_id} highlight />
            <KV k="provider msg id" v={row.data?.provider_msg_id} />
          </Section>
        )}
      </div>
    </div>
  );
}


function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
        {title}
      </div>
      <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 space-y-1.5">
        {children}
      </div>
    </div>
  );
}


function KV({ k, v, highlight }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className={`text-right font-mono ${highlight ? "text-red-600 font-semibold" : "text-slate-900"}`}>
        {v == null || v === "" ? "—" : String(v)}
      </span>
    </div>
  );
}


// ===========================================================================
// Email Template Gallery
// ===========================================================================

function TemplateGallery() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/admin/notifications/templates")
      .then((r) => {
        setTemplates(r.data || []);
        if ((r.data || []).length) setSelected(r.data[0].event_type);
      })
      .catch((e) => toast.error(e.message || "Failed to load templates"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setPreview(null);
    api(`/admin/notifications/templates/${selected}/preview`,
        { method: "POST", body: {} })
      .then(setPreview)
      .catch((e) => toast.error(e.message || "Could not render template"));
  }, [selected]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">
          Email template gallery
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Read-only preview of every Jinja2 template rendered with a synthetic
          context. Subject lines, HTML, and plain-text bodies are all surfaced.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[70vh]">
        <aside className="col-span-1 lg:col-span-4 flex flex-col gap-2 max-h-[70vh] overflow-y-auto pr-2 border-r border-slate-200">
          <div data-testid={TID.adminTemplatesList} className="flex flex-col gap-2">
            {loading && <div className="text-sm text-slate-500 p-4">Loading templates…</div>}
            {!loading && templates.length === 0 && (
              <div className="text-sm text-slate-500 p-4">No templates found.</div>
            )}
            {templates.map((t) => (
              <button
                key={t.event_type}
                type="button"
                data-testid={TID.adminTemplateRow}
                onClick={() => setSelected(t.event_type)}
                className={`text-left p-3 rounded-lg border transition ${
                  selected === t.event_type
                    ? "border-[var(--avcp-primary)] bg-[var(--avcp-primary)]/5 ring-1 ring-[var(--avcp-primary)]"
                    : "border-slate-200 bg-white hover:border-[var(--avcp-primary)]"
                }`}
              >
                <div className="font-mono text-sm font-medium text-slate-900">
                  {t.event_type}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {t.kinds.join(" · ")}
                </div>
              </button>
            ))}
          </div>
        </aside>

        <div className="col-span-1 lg:col-span-8 flex flex-col gap-3">
          {preview ? (
            <>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Subject
                </div>
                <div className="font-medium text-slate-900">{preview.subject}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex-1 min-h-[400px]">
                <div className="px-4 py-2 bg-white border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  HTML preview
                </div>
                <iframe
                  data-testid={TID.adminTemplatePreviewIframe}
                  title="email-html-preview"
                  srcDoc={preview.html}
                  className="w-full h-[500px] bg-white"
                  sandbox=""
                />
              </div>
              <details className="rounded-lg border border-slate-200 bg-white p-4">
                <summary className="cursor-pointer text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  Plain-text body
                </summary>
                <pre className="text-sm font-mono text-slate-700 whitespace-pre-wrap mt-3">
                  {preview.text}
                </pre>
              </details>
            </>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              Select a template to preview.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
